import {
  adaptDealToFlightOffer,
  buildCombinedSelfComposeFlightOffer,
  countTotalPaxFromRoomInfo,
} from "../../adapters/flightOfferAdapter";
import { resolveFlightHrCommissionAmount } from "../../../../pricing/flightHrCommission";

export function dealKey(deal) {
  return String(deal?.dealId ?? deal?.ID ?? "");
}

export function legAdultFare(deal) {
  const n = Number(deal?.dealFare?.adultFare);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function legCurrency(deal) {
  const c = deal?.fareDetails?.currency;
  return c != null && String(c).trim() !== "" ? String(c).trim() : "";
}

function roundMoney(n) {
  if (!Number.isFinite(n)) return n;
  return Math.round(n * 100) / 100;
}

/** נוסעים לתמחור SSOT: טופס אם קיים, אחרת roomInfo, אחרת 1 */
export function resolveSelfComposePassengerCount(roomInfo, passengersLength = 0) {
  const fromRooms = countTotalPaxFromRoomInfo(roomInfo);
  if (passengersLength > 0) return passengersLength;
  if (fromRooms != null && fromRooms > 0) return fromRooms;
  return 1;
}

/**
 * מקור אמת יחיד למחיר הרכבה עצמית (הלוך±חזור): סה״כ הזמנה ומחיר לאדם = סה״כ / מספר נוסעים.
 * flightsTotal = מחיר טיסות בלבד (לפי totalFare לכל כיוון או adultFare×נוסעים); baggageRaw נוסף לסה״כ.
 * תומך גם בהלוך בלבד או חזור בלבד (לפני הצעה משולבת).
 */
export function computeSelfComposePricingSsot(
  outDeal,
  inDeal,
  roomInfo,
  baggageRaw = 0,
  passengersLength = 0
) {
  if (!outDeal && !inDeal) return null;
  const pax = resolveSelfComposePassengerCount(roomInfo, passengersLength);
  const cur = legCurrency(outDeal) || legCurrency(inDeal) || "";

  let flightsTotal = null;
  let outboundPriceSource = null;
  let inboundPriceSource = null;

  if (outDeal && inDeal) {
    const outTf = Number(outDeal?.dealFare?.totalFare);
    const inTf = Number(inDeal?.dealFare?.totalFare);
    const a = legAdultFare(outDeal);
    const b = legAdultFare(inDeal);

    if (Number.isFinite(outTf) && Number.isFinite(inTf)) {
      flightsTotal = outTf + inTf;
      outboundPriceSource = "dealFare.totalFare";
      inboundPriceSource = "dealFare.totalFare";
    } else if (a != null && b != null) {
      flightsTotal = (a + b) * pax;
      outboundPriceSource = "dealFare.adultFare×pax";
      inboundPriceSource = "dealFare.adultFare×pax";
    } else if (Number.isFinite(outTf) && b != null) {
      flightsTotal = outTf + b * pax;
      outboundPriceSource = "dealFare.totalFare";
      inboundPriceSource = "dealFare.adultFare×pax";
    } else if (Number.isFinite(inTf) && a != null) {
      flightsTotal = a * pax + inTf;
      outboundPriceSource = "dealFare.adultFare×pax";
      inboundPriceSource = "dealFare.totalFare";
    }
  } else {
    const deal = outDeal || inDeal;
    const isOut = Boolean(outDeal);
    const tf = Number(deal?.dealFare?.totalFare);
    const adult = legAdultFare(deal);
    if (Number.isFinite(tf)) {
      flightsTotal = tf;
      if (isOut) outboundPriceSource = "dealFare.totalFare";
      else inboundPriceSource = "dealFare.totalFare";
    } else if (adult != null) {
      flightsTotal = adult * pax;
      if (isOut) outboundPriceSource = "dealFare.adultFare×pax";
      else inboundPriceSource = "dealFare.adultFare×pax";
    }
  }

  if (flightsTotal == null || !Number.isFinite(flightsTotal)) return null;

  const baggage = Number(baggageRaw);
  const baggageAdd = Number.isFinite(baggage) && baggage > 0 ? baggage : 0;
  const totalBooking = roundMoney(flightsTotal + baggageAdd);
  const totalPerPerson = roundMoney(totalBooking / Math.max(1, pax));

  return {
    flightsTotal: roundMoney(flightsTotal),
    totalBooking,
    totalPerPerson,
    passengerCount: pax,
    currency: cur,
    baggageAdd,
    outboundPriceSource,
    inboundPriceSource,
  };
}

export function formatSsotTotalBookingLine(ssot) {
  if (!ssot) return null;
  const c = ssot.currency;
  const t = ssot.totalBooking;
  return c ? `${t} ${c}` : String(t);
}

function fmtSsotAmount(n) {
  if (!Number.isFinite(Number(n))) return String(n);
  const x = Number(n);
  return Math.abs(x - Math.round(x)) < 0.001 ? String(Math.round(x)) : String(Math.round(x * 100) / 100);
}

/**
 * שורת סה״כ לתשלום (אותו totalBooking כמו ב-SSOT).
 * @param {{ totalBooking: number, currency?: string, passengerCount?: number, paxCount?: number }} ssot
 */
export function formatSelfComposeTotalPaySubline(ssot) {
  if (!ssot || !Number.isFinite(Number(ssot.totalBooking))) return null;
  const cur = String(ssot.currency || "").trim();
  const t = fmtSsotAmount(ssot.totalBooking);
  const n = Math.max(
    1,
    Math.floor(
      Number(ssot.passengerCount ?? ssot.paxCount ?? 1) || 1
    )
  );
  if (n > 1) {
    return cur
      ? `סה״כ לתשלום: ${t} ${cur} ל-${n} נוסעים`
      : `סה״כ לתשלום: ${t} ל-${n} נוסעים`;
  }
  return cur ? `סה״כ לתשלום: ${t} ${cur}` : `סה״כ לתשלום: ${t}`;
}

/**
 * ממלא שדות תצוגה בהצעה משולבת רק מ-computeSelfComposePricingSsot (לא מסתמך על adultFare של ה-deal הסינתטי).
 */
function resolveSelfComposeClientPerPersonColumns(ssot, currencyHint, offer) {
  const raw = String(currencyHint || offer?.rawCurrency || "").trim().toUpperCase();
  const isUsd =
    raw === "USD" ||
    raw === "US$" ||
    raw === "$" ||
    raw === "DOLLAR" ||
    raw === "DOLLARS";
  const isIls =
    raw === "ILS" ||
    raw === "NIS" ||
    raw === "ש\"ח" ||
    raw === "שח";
  const per = ssot.totalPerPerson;
  if (!Number.isFinite(per)) {
    return {
      priceClientPerPersonUsd: offer.priceClientPerPersonUsd,
      priceClientPerPersonIls: offer.priceClientPerPersonIls,
    };
  }
  if (isUsd) {
    return {
      priceClientPerPersonUsd: per,
      priceClientPerPersonIls: offer.priceClientPerPersonIls,
    };
  }
  if (isIls) {
    return {
      priceClientPerPersonUsd: offer.priceClientPerPersonUsd,
      priceClientPerPersonIls: Math.ceil(per),
    };
  }
  return {
    priceClientPerPersonUsd: offer.priceClientPerPersonUsd,
    priceClientPerPersonIls: offer.priceClientPerPersonIls,
  };
}

/**
 * מחיר לקוח ממונע (ourPrice) — אותו שדה שהכרטיס מציג, לא dealFare גולמי.
 */
export function readEngineClientPerPersonUsd(offer) {
  const n = Number(offer?.priceClientPerPersonUsd);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function callDealFareCalc(calc, deal) {
  if (typeof calc !== "function" || !deal) return null;
  try {
    return calc(deal);
  } catch {
    return null;
  }
}

function rawSsotPerPersonUsd(outDeal, inDeal, roomInfo, passengersLength = 0) {
  const ssot = computeSelfComposePricingSsot(
    outDeal,
    inDeal,
    roomInfo,
    0,
    passengersLength
  );
  if (!ssot || !Number.isFinite(ssot.totalPerPerson)) return null;
  const cur = String(ssot.currency || "").trim().toUpperCase();
  if (cur && cur !== "USD" && cur !== "$" && cur !== "US$" && cur !== "DOLLAR" && cur !== "DOLLARS") {
    return null;
  }
  return ssot.totalPerPerson;
}

function looksLikeRawGrossPerPerson(enginePer, rawPer) {
  if (enginePer == null || rawPer == null) return enginePer == null;
  return Math.abs(Number(enginePer) - Number(rawPer)) <= 0.051;
}

function readEngineHrPercent(ourPrice) {
  const raw =
    ourPrice?.ourTotalFare?.unifiedHrMarginPercent ??
    ourPrice?.ourTotalFare?.origCurrency?.unifiedHrMarginPercent ??
    ourPrice?.ourTotalFare?.hrCommissionPercent ??
    ourPrice?.ourTotalFare?.origCurrency?.hrCommissionPercent;
  if (raw == null || raw === "") return null;
  const p = Number(raw);
  return Number.isFinite(p) && p >= 0 ? p : null;
}

function readEngineNetPerPersonUsd(ourPrice, pax) {
  const nPax = Math.max(1, Number(pax) || 1);
  const orig = ourPrice?.ourTotalFare?.origCurrency;
  const netTotal = Number(orig?.netPrice ?? orig?.initialPrice);
  if (Number.isFinite(netTotal) && netTotal > 0) return netTotal / nPax;
  return null;
}

/**
 * Self-compose RT: HR is max(% of that OW, $30) on each direction, then summed.
 * Cheap charter both ways → $60/pax, not $30 total.
 */
export function resolveSelfComposeItineraryClientPerPersonUsd({
  outClientPerPax,
  inClientPerPax,
  outNetPerPax,
  inNetPerPax,
  hrPercent,
} = {}) {
  const outN = Number(outNetPerPax);
  const inN = Number(inNetPerPax);
  const pct = Number(hrPercent);
  if (
    Number.isFinite(outN) &&
    outN > 0 &&
    Number.isFinite(inN) &&
    inN > 0 &&
    Number.isFinite(pct) &&
    pct >= 0
  ) {
    const hrOut = resolveFlightHrCommissionAmount({
      percentAmount: (outN * pct) / 100,
      passengersCount: 1,
      directionCount: 1,
    });
    const hrIn = resolveFlightHrCommissionAmount({
      percentAmount: (inN * pct) / 100,
      passengersCount: 1,
      directionCount: 1,
    });
    return roundMoney(outN + inN + hrOut + hrIn);
  }
  const outC = Number(outClientPerPax);
  const inC = Number(inClientPerPax);
  if (Number.isFinite(outC) && outC > 0 && Number.isFinite(inC) && inC > 0) {
    return roundMoney(outC + inC);
  }
  return null;
}

function areOwClientPricesIndependent(outUsd, inUsd, combinedUsd) {
  if (!(Number(outUsd) > 0) || !(Number(inUsd) > 0)) return false;
  const combined = Number(combinedUsd);
  if (Number.isFinite(combined) && combined > 0) {
    if (Math.abs(Number(outUsd) - combined) <= 0.51 && Math.abs(Number(inUsd) - combined) <= 0.51) {
      return false;
    }
  }
  return true;
}

function ilsSumScaledToItineraryUsd(outIls, inIls, outUsd, inUsd, itineraryUsd) {
  const ils = Number(outIls) + Number(inIls);
  const usdSum = Number(outUsd) + Number(inUsd);
  const per = Number(itineraryUsd);
  if (!Number.isFinite(ils) || ils <= 0) return null;
  if (!Number.isFinite(usdSum) || usdSum <= 0 || !Number.isFinite(per) || per <= 0) {
    return ils;
  }
  if (Math.abs(usdSum - per) <= 0.051) return ils;
  return roundMoney(ils * (per / usdSum));
}

/**
 * סה״כ מפלגה לתצוגה: Math.round(per-person) × נוסעים — תואם כרטיס תוצאה.
 */
export function formatSelfComposeEnginePartyTotalLabel(offer) {
  const per = readEngineClientPerPersonUsd(offer);
  const pax = Number(offer?.totalPaxCount);
  if (per == null || !Number.isFinite(pax) || pax <= 0) return null;
  const total = Math.round(per) * pax;
  const c = String(offer?.rawCurrency || "").trim();
  return c ? `${total} ${c}` : String(total);
}

function enginePartyTotalUsd(offer, passengerCount) {
  const per = readEngineClientPerPersonUsd(offer);
  if (per == null) return null;
  const pax = Math.max(1, Number(passengerCount) || Number(offer?.totalPaxCount) || 1);
  return Math.round(per) * pax;
}

/** מחליף SSOT גולמי במחיר ממונע כשההצעה כבר תומחרה ב-ourPrice. */
export function overlayEngineClientOnSelfComposeSsot(ssot, offer) {
  const engineUsd = readEngineClientPerPersonUsd(offer);
  if (!ssot || engineUsd == null) return ssot;
  const engineTotal = enginePartyTotalUsd(offer, ssot.passengerCount);
  if (engineTotal == null) return ssot;
  return {
    ...ssot,
    source: "engine",
    totalPerPerson: engineUsd,
    flightsTotal: engineTotal,
    totalBooking: roundMoney(engineTotal + (ssot.baggageAdd || 0)),
  };
}

/**
 * הצעה משולבת עם ourPrice ממונע. אם העסקה הסינתטית נכשלת — סכום שני כיווני OW
 * שמתומחרים בנפרד (ספקים מעורבים).
 */
export function buildPricedSelfComposeCombinedOffer(
  outDeal,
  inDeal,
  roomInfo,
  calculateLDSDealFareForRoomsComposition,
  options = {}
) {
  if (!outDeal || !inDeal) return null;
  const calc =
    typeof calculateLDSDealFareForRoomsComposition === "function"
      ? calculateLDSDealFareForRoomsComposition
      : null;
  const baggageRaw = options.baggageRaw ?? 0;
  const passengersLength = options.passengersLength ?? 0;

  const seed = buildCombinedSelfComposeFlightOffer(outDeal, inDeal, roomInfo);
  const combinedOurPrice =
    seed?.rawDeal && calc
      ? callDealFareCalc(calc, seed.rawDeal)
      : options.ourPrice ?? null;
  let combined = buildCombinedSelfComposeFlightOffer(outDeal, inDeal, roomInfo, {
    ourPrice: combinedOurPrice,
  });

  const combinedUsd = readEngineClientPerPersonUsd(combined);
  const rawPer = rawSsotPerPersonUsd(outDeal, inDeal, roomInfo, passengersLength);
  const combinedLooksRaw = looksLikeRawGrossPerPerson(combinedUsd, rawPer);

  if (calc) {
    const outOur = callDealFareCalc(calc, outDeal);
    const inOur = callDealFareCalc(calc, inDeal);
    const outOffer = adaptDealToFlightOffer(outDeal, 0, roomInfo, {
      ourPrice: outOur,
    });
    const inOffer = adaptDealToFlightOffer(inDeal, 1, roomInfo, {
      ourPrice: inOur,
    });
    const outUsd = readEngineClientPerPersonUsd(outOffer);
    const inUsd = readEngineClientPerPersonUsd(inOffer);
    if (outUsd != null && inUsd != null) {
      const pax = Math.max(
        1,
        Number(combined?.totalPaxCount) ||
          resolveSelfComposePassengerCount(roomInfo, passengersLength)
      );
      const itineraryUsd = resolveSelfComposeItineraryClientPerPersonUsd({
        outClientPerPax: outUsd,
        inClientPerPax: inUsd,
        outNetPerPax: readEngineNetPerPersonUsd(outOur, pax),
        inNetPerPax: readEngineNetPerPersonUsd(inOur, pax),
        hrPercent: readEngineHrPercent(outOur) ?? readEngineHrPercent(inOur),
      });
      const per = itineraryUsd ?? roundMoney(outUsd + inUsd);
      const independent = areOwClientPricesIndependent(outUsd, inUsd, combinedUsd);
      const preferOwSum =
        independent &&
        (combinedUsd == null ||
          combinedLooksRaw ||
          itineraryUsd != null);
      if (preferOwSum) {
        const outIls = Number(outOffer.priceClientPerPersonIls);
        const inIls = Number(inOffer.priceClientPerPersonIls);
        const ilsSum =
          Number.isFinite(outIls) && Number.isFinite(inIls) && outIls > 0 && inIls > 0
            ? ilsSumScaledToItineraryUsd(outIls, inIls, outUsd, inUsd, per)
            : null;
        combined = {
          ...(combined || {}),
          isSelfComposeCombined: true,
          source: "selfCompose",
          selfComposeLegs: { outboundRawDeal: outDeal, inboundRawDeal: inDeal },
          priceClientPerPersonUsd: per,
          priceClientPerPersonIls: ilsSum,
          totalPaxCount: pax,
          rawCurrency: combined?.rawCurrency || outOffer.rawCurrency || inOffer.rawCurrency,
          originalCurrency: combined?.originalCurrency || outOffer.originalCurrency || inOffer.originalCurrency || combined?.rawCurrency || outOffer.rawCurrency || inOffer.rawCurrency,  // ← חדש!
        };
      }
    }
  }

  if (!combined) return null;
  return withSelfComposeOfferSsot(
    combined,
    outDeal,
    inDeal,
    roomInfo,
    baggageRaw,
    passengersLength
  );
}

/**
 * מחיל SSOT ממונע על הצעה משולבת כשיש priceClientPerPersonUsd; לא דורס אותו במחיר גולמי.
 */
export function withSelfComposeOfferSsot(
  offer,
  outDeal,
  inDeal,
  roomInfo,
  baggageRaw = 0,
  passengersLength = 0
) {
  if (!offer?.isSelfComposeCombined || !outDeal || !inDeal) return offer;
  const ssot = computeSelfComposePricingSsot(
    outDeal,
    inDeal,
    roomInfo,
    baggageRaw,
    passengersLength
  );
  if (!ssot) return offer;
  const fmt = (x) =>
    Math.abs(x - Math.round(x)) < 0.001 ? Math.round(x) : Math.round(x * 100) / 100;
  const engineUsd = readEngineClientPerPersonUsd(offer);
  const hasEngineClientPrice = engineUsd != null;
  if (hasEngineClientPrice) {
    const cur = (offer.rawCurrency || ssot.currency || "").trim();
    const engineTotal =
      enginePartyTotalUsd(offer, ssot.passengerCount) ?? ssot.totalBooking;
    const engineBooking = roundMoney(engineTotal + (ssot.baggageAdd || 0));
    return {
      ...offer,
      selfComposePricingSsot: {
        ...ssot,
        source: "engine",
        totalPerPerson: engineUsd,
        flightsTotal: engineTotal,
        totalBooking: engineBooking,
      },
      pricePerPerson: engineUsd,
      pricePerPersonDisplayLabel: cur
        ? `${fmt(engineUsd)} ${cur} לאדם`
        : `${fmt(engineUsd)} לאדם`,
      displayedPrice: engineUsd,
      totalFare: engineBooking,
      totalFareDisplayLabel: cur
        ? `${fmt(engineBooking)} ${cur}`
        : String(fmt(engineBooking)),
      priceUsd: engineBooking,
      priceClientPerPersonUsd: engineUsd,
      rawCurrency: cur || offer.rawCurrency,
      originalCurrency: cur || offer.originalCurrency || offer.rawCurrency,  // ← חדש!
      resultsTotalFareSubline:
        formatSelfComposeTotalPaySubline({
          ...ssot,
          totalBooking: engineBooking,
          currency: cur,
        }) || offer.resultsTotalFareSubline,
    };
  }
  const cur = (ssot.currency || offer.rawCurrency || "").trim();
  const subline = formatSelfComposeTotalPaySubline(ssot);
  const clientCols = resolveSelfComposeClientPerPersonColumns(ssot, cur, offer);
  return {
    ...offer,
    selfComposePricingSsot: ssot,
    pricePerPerson: ssot.totalPerPerson,
    pricePerPersonDisplayLabel: cur
      ? `${fmt(ssot.totalPerPerson)} ${cur} לאדם`
      : `${fmt(ssot.totalPerPerson)} לאדם`,
    resultsTotalFareSubline: subline,
    totalFare: ssot.totalBooking,
    totalFareDisplayLabel: cur
      ? `${fmt(ssot.totalBooking)} ${cur}`
      : String(fmt(ssot.totalBooking)),
    displayedPrice: ssot.totalPerPerson,
    rawCurrency: cur || offer.rawCurrency,
    priceUsd: ssot.totalBooking,
    priceClientPerPersonUsd: clientCols.priceClientPerPersonUsd,
    priceClientPerPersonIls: clientCols.priceClientPerPersonIls,
  };
}

/**
 * סיכום שורה לפס סיכום / כרטיס — מבוסס adaptDealToFlightOffer.
 * @param {{ legSegmentMode?: boolean }} [options] — כשמציגים מחיר לכיוון OW בלבד, מוסיף סימון ברור
 */
export function summarizeDealForBar(deal, roomInfo, options = {}) {
  if (!deal) {
    return {
      route: null,
      dateLabel: null,
      timesLabel: null,
      priceLabel: null,
    };
  }
  const o = adaptDealToFlightOffer(deal, 0, roomInfo);
  const m = o.cardMeta;
  const route =
    m?.originCode && m?.destinationCode
      ? `${m.originCode} → ${m.destinationCode}`
      : o.route || null;
  const dateLabel = m?.depDateDisplay || null;
  const timesLabel =
    m?.departureTime && m?.arrivalTime
      ? `${m.departureTime}–${m.arrivalTime}`
      : m?.timesLabel || null;
  let priceLabel = null;
  if (o.pricePerPerson != null && o.rawCurrency) {
    priceLabel = `${o.pricePerPerson} ${o.rawCurrency}`;
  } else if (o.pricePerPersonDisplayLabel && o.pricePerPersonDisplayLabel !== "—") {
    priceLabel = o.pricePerPersonDisplayLabel.replace(/\s*לאדם\s*$/, "").trim();
  }
  if (options.legSegmentMode && priceLabel) {
    priceLabel = `${priceLabel} · מחיר לכיוון אחד`;
  }
  return { route, dateLabel, timesLabel, priceLabel, offer: o };
}

/** סה״כ טיסות (כל הנוסעים, הלוך+חזור), ללא כבודה — מחרוזת תצוגה */
export function formatCombinedTotal(outDeal, inDeal, roomInfo) {
  const ssot = computeSelfComposePricingSsot(outDeal, inDeal, roomInfo, 0, 0);
  if (!ssot) return null;
  const c = ssot.currency;
  const t = ssot.flightsTotal;
  return c ? `${t} ${c}` : String(t);
}

export function formatInterimOutboundOnly(outDeal, roomInfo) {
  if (!outDeal) return null;
  const pax = resolveSelfComposePassengerCount(roomInfo, 0);
  const tf = Number(outDeal?.dealFare?.totalFare);
  const a = legAdultFare(outDeal);
  const cur = legCurrency(outDeal);
  let amount = null;
  if (Number.isFinite(tf)) amount = tf;
  else if (a != null) amount = roundMoney(a * pax);
  if (amount == null) return null;
  return cur ? `${amount} ${cur}` : String(amount);
}

/** שורה אחת לסיכום חי — מסלול · תאריך · שעות · מחיר leg (מסומן ככיוון אחד) */
export function formatSelfComposeLegLine(deal, roomInfo) {
  const s = summarizeDealForBar(deal, roomInfo, { legSegmentMode: true });
  if (!s.route) return null;
  return [s.route, s.dateLabel, s.timesLabel, s.priceLabel]
    .filter(Boolean)
    .join(" · ");
}

/**
 * מחירי סיכום חי בהרכבה — תמיד מבוסס SSOT כשיש שני כיוונים; לאדם = סה״כ/נוסעים.
 * @param {number} baggageRaw — סכום כבודה גולמי (מ־LDS)
 */
export function computeSelfComposeLivePricing(
  outDeal,
  inDeal,
  roomInfo,
  baggageRaw = 0,
  passengersLength = 0
) {
  const cur =
    legCurrency(outDeal || {}) || legCurrency(inDeal || {}) || "";
  const pax = resolveSelfComposePassengerCount(roomInfo, passengersLength);

  if (outDeal && inDeal) {
    const ssot = computeSelfComposePricingSsot(
      outDeal,
      inDeal,
      roomInfo,
      baggageRaw,
      passengersLength
    );
    if (ssot) {
      const per = ssot.totalPerPerson;
      return {
        perPersonStr: cur ? `${per} ${cur}` : String(per),
        totalStr: formatSelfComposeTotalPaySubline(ssot),
      };
    }
  }

  if ((outDeal && !inDeal) || (!outDeal && inDeal)) {
    const ssotOne = computeSelfComposePricingSsot(
      outDeal,
      inDeal,
      roomInfo,
      baggageRaw,
      passengersLength
    );
    if (ssotOne) {
      const per = ssotOne.totalPerPerson;
      return {
        perPersonStr: cur ? `${per} ${cur}` : String(per),
        totalStr: formatSelfComposeTotalPaySubline(ssotOne),
      };
    }
  }

  return { perPersonStr: null, totalStr: null };
}
